import { Spacing, Text } from '@vkontakte/vkui';
import React, { type FC, memo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { aiPhotoGenerationTariffsModal } from 'reactApp/appHelpers/featuresHelpers/features/aiPhotoGeneration';
import { cdnPackagesUrl } from 'reactApp/appHelpers/themeToolHelpers/default-values';
import { WhatsNewDialog } from 'reactApp/components/WhatsNewDialog/WhatsNewDialog';
import { useNextTime } from 'reactApp/modules/aiPhotoGeneration/aiPhotoGeneration.clamp.hooks';
import { useTariffBuy } from 'reactApp/modules/payment/hooks/useTariffBuy';
import { openBuyDisableAds } from 'reactApp/modules/payment/payment.module';
import { sendXray } from 'reactApp/utils/ga';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';

import styles from './AiPhotoGenerationMailSpacePromo.css';

interface Props {
    onClose?: () => void;
}

const name = 'ai-gen-mail-space-promo-modal';

const modal_type = 'try-mail-space';

const handleDismiss = () => {
    sendXray([name, 'close']);

    sendPaymentGa({
        eventCategory: ECategoryGa.aiPhotoGeneration,
        action: 'modal-close',
        modal_type,
        dwh_enabled: true,
        dwh_enabled_fields: [['modal_type']],
    });
};

export const AiPhotoGenerationMailSpacePromo: FC<Props> = memo(function AiPhotoGenerationMailSpacePromo({ onClose }: Props) {
    const dispatch = useDispatch();

    const onClickBuy = useTariffBuy({
        paySource: aiPhotoGenerationTariffsModal.paySource,
    });

    const handleClose = useCallback(() => {
        onClose?.();
    }, [onClose]);

    const handleShow = useCallback(() => {
        sendXray([name, 'show']);

        sendPaymentGa({
            eventCategory: ECategoryGa.aiPhotoGeneration,
            action: 'modal-show',
            modal_type,
            dwh_enabled: true,
            dwh_enabled_fields: [['modal_type']],
        });
    }, []);

    const handleClick = useCallback(() => {
        sendXray([name, 'click']);

        sendPaymentGa({
            eventCategory: ECategoryGa.aiPhotoGeneration,
            action: 'modal-click',
            modal_type,
            action_type: 'success',
            dwh_enabled: true,
            dwh_enabled_fields: [['modal_type']],
        });

        dispatch(
            openBuyDisableAds({
                title: aiPhotoGenerationTariffsModal.buyTitle,
                tariffsNumber: aiPhotoGenerationTariffsModal.tariffsNumber,
                activeTab: aiPhotoGenerationTariffsModal.activeTab,
                isFrame: false,
                onBuyClick: onClickBuy,
                actionShow: aiPhotoGenerationTariffsModal.actionShow,
                actionClick: aiPhotoGenerationTariffsModal.actionClick,
            })
        );
    }, [dispatch, onClickBuy]);

    const { data: nextTime } = useNextTime();

    return (
        <WhatsNewDialog
            imageUrl={`${cdnPackagesUrl}/static/cloud/promo/aiPhotoGeneration/home/modal.webp`}
            imageWrapperClassName={styles.imageWrapper}
            contentClassName={styles.content}
            title="Если генерация понравилась, можно создать ещё"
            content={
                <>
                    <Spacing size={16} />
                    <Text data-qa-id="text">
                        Без&nbsp;подписки можно создать новое изображение с&nbsp;нейросетью через&nbsp;24&nbsp;часа
                        {nextTime ? `, в\u00A0${nextTime?.time}` : ''}. А&nbsp;с&nbsp;подпиской&nbsp;— уже&nbsp;сейчас
                    </Text>
                    <Spacing size={32} />
                </>
            }
            primaryButtonText="Продолжить за 1р"
            closeAfterClick
            onClick={handleClick}
            onShow={handleShow}
            onDismiss={handleDismiss}
            onClose={handleClose}
            imageBgColor="var(--vkui--color_background_modal)"
            buttonTheme="vk"
            dialogSize="small"
            qaId="ai-photo-generation-mail-space-promo-modal"
            isRebranding
        />
    );
});

AiPhotoGenerationMailSpacePromo.displayName = 'AiPhotoGenerationMailSpacePromo';

export default AiPhotoGenerationMailSpacePromo;
